import { Box, Button, Container, Paper, Tab, Tabs, Typography } from '@mui/material';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { BarChart3, FileText } from 'lucide-react';
import { format } from 'date-fns';
import { FC, memo, useCallback, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { appBarHeight } from 'src/constants/layout';
import { useTransportSystemLedgerShipperOptionsQuery } from 'src/hooks/useTransportSystemLedgerShipperOptions.query';
import ShipperSelectDialog from 'src/pages/V3Plans/presenters/ShipperSelectDialog';

import BillingPerformanceSection from './BillingPerformanceSection';
import { useTransportSystemLedgerExport } from './useTransportSystemLedgerExport';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: FC<TabPanelProps> = ({ children, value, index }) => (
  <div
    role="tabpanel"
    hidden={value !== index}
    id={`performance-tabpanel-${index}`}
    aria-labelledby={`performance-tab-${index}`}
    style={value === index ? { display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 } : undefined}
  >
    {value === index && (
      <Box sx={{ pt: 1, pb: 2, display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
        {children}
      </Box>
    )}
  </div>
);

const PerformanceManagementPresenter: FC = memo(() => {
  const [currentTab, setCurrentTab] = useState(0);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setCurrentTab(newValue);
  };

  const [ledgerRange, setLedgerRange] = useState<{ start: Date | null; end: Date | null }>({ start: null, end: null });
  const handleDateRangeChange = useCallback(
    (start: Date | null, end: Date | null) => setLedgerRange({ start, end }),
    [],
  );
  const startOn = ledgerRange.start ? format(ledgerRange.start, 'yyyy-MM-dd') : '';
  const endOn = ledgerRange.end ? format(ledgerRange.end, 'yyyy-MM-dd') : '';
  const {
    dialogOpen, openDialog, closeDialog, handleConfirm,
  } = useTransportSystemLedgerExport({ startOn, endOn });
  const shipperOptionsQuery = useTransportSystemLedgerShipperOptionsQuery({ startOn, endOn, enabled: dialogOpen });

  return (
    <>
      <Helmet>
        <title>実績管理 - LOG</title>
      </Helmet>
      <Box
        sx={{
          bgcolor: 'background.paper',
          height: `calc(100vh - ${appBarHeight}px)`,
          display: 'flex',
          flexDirection: 'column',
          p: 1,
          overflow: 'hidden',
        }}
      >
        <Container
          maxWidth={false}
          disableGutters
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
            flex: 1,
            mb: 1,
            minHeight: 0,
            overflow: 'hidden',
          }}
        >
          {/* タイトル */}
          <Box display="flex" alignItems="center" gap={2} sx={{ height: 70 }}>
            <Box
              sx={{
                p: 1,
                bgcolor: 'primary.main',
                borderRadius: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <BarChart3 size={20} color="white" />
            </Box>
            <Typography variant="h3" fontWeight="bold">
              実績管理
            </Typography>
          </Box>

          <Paper
            sx={{
              flex: 1,
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
              minHeight: 0,
            }}
          >
            <Box
              sx={{
                borderBottom: 1,
                borderColor: 'divider',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                pr: 2,
              }}
            >
              <Tabs value={currentTab} onChange={handleTabChange}>
                <Tab
                  label={(
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <FileText size={18} />
                      請求・支払実績
                    </Box>
                  )}
                />
              </Tabs>
              <Button
                size="small"
                variant="outlined"
                startIcon={<FileDownloadIcon />}
                onClick={openDialog}
                disabled={!startOn || !endOn}
              >
                実運送体制管理簿
              </Button>
            </Box>

            <Box sx={{ flex: 1, minHeight: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column', px: 2 }}>
              <TabPanel value={currentTab} index={0}>
                <BillingPerformanceSection onDateRangeChange={handleDateRangeChange} />
              </TabPanel>
            </Box>
          </Paper>
        </Container>
      </Box>
      <ShipperSelectDialog
        open={dialogOpen}
        shipperNames={shipperOptionsQuery.data?.names ?? []}
        isLoading={shipperOptionsQuery.isLoading}
        isError={shipperOptionsQuery.isError}
        onBack={closeDialog}
        onConfirm={(selected) => handleConfirm(selected)}
      />
    </>
  );
});

export default PerformanceManagementPresenter;
